import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, LogOut } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import ProfileCard from "@/components/settings/ProfileCard";
import StateSection from "@/components/settings/StateSection";
import LanguageSection from "@/components/settings/LanguageSection";
import TextSizeSection from "@/components/settings/TextSizeSection";
import AutoContributeSection from "@/components/settings/AutoContributeSection";
import SharedListsSection from "@/components/settings/SharedListsSection";
import MaintenanceSection from "@/components/settings/MaintenanceSection";
import DownloadLibrarySection from "@/components/settings/DownloadLibrarySection";

/**
 * Settings — slim orchestrator. Each section is a self-contained
 * component in `@/components/settings/*` that owns its own state,
 * dialogs, and API calls. Adding a new preference means adding one
 * more <Section/> tag here — no more scrolling through a 1200-line
 * page to find where language state lives.
 *
 * Layout note: the outer div constrains to `md:max-w-3xl` so the
 * page reads as a comfortable single column on desktop instead of
 * stretching edge-to-edge.
 */
export default function Settings() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { t } = useTranslation();

  return (
    <AppShell>
      <div className="md:max-w-3xl md:mx-auto">
        <div className="px-5 pt-1 flex items-center gap-3" data-testid="settings-page">
          <button
            onClick={() => navigate(-1)}
            aria-label={t("settings.backAria")}
            data-testid="settings-back"
            className="w-9 h-9 rounded-full bg-white border border-[#E8E5DF] flex items-center justify-center text-leaf-ink"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <h1 className="font-display text-3xl text-leaf-ink leading-tight">
            {t("settings.title")}
          </h1>
        </div>

        <div className="px-5 mt-6 space-y-5 pb-2">
          <ProfileCard />
          <StateSection />
          <LanguageSection />
          <TextSizeSection />
          <AutoContributeSection />
          <SharedListsSection />
          <MaintenanceSection />
          <DownloadLibrarySection />

          <button
            onClick={async () => { await signOut(); navigate("/login"); }}
            data-testid="settings-sign-out"
            className="w-full rounded-full py-3.5 bg-parchment text-rust font-medium hover:bg-[#EEE9DE] transition active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t("settings.signOut")}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
